import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Case, Default, Switch, When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { LinkButton } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import {
  AsH1,
  AsH2,
  AsSpan,
  CaptionMD,
  HeadingXS,
  LabelXS,
  ParagraphMD,
  ParagraphSM,
  TitleLG,
  TitleXS,
} from '@/atoms/Text'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { useLegacyEffect } from '@/molecules/utils'
import {
  DiscoverItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { buildLinkTabModel, ComingSoonNode } from '@/services/ComingSoonService'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '../GuildJoinView/GuildEmailInput'

interface ComingSoonViewProps {
  comingSoonNodes: ComingSoonNode[]
}

export const ComingSoonView: React.FC<ComingSoonViewProps> = ({ comingSoonNodes }) => {
  const { t } = useTranslate('coming-soon')
  const track = useSafeTrack()
  const { isLoggedIn } = useUser()
  const { isGuildMember } = useGuildMember()
  const { loginUrl, trackLoginStarted } = useLoginUrl()

  useLegacyEffect(() => {
    track('Coming Soon Page Viewed')
  }, [track])

  return (
    <main className="bg-core-gray-950 pb-32 pt-16 text-white">
      <PaddedContainer>
        <div className="flex flex-col gap-2 pt-8 lg:pt-12">
          <TitleLG className="lg:photon-heading-lg" as={AsH1} weight="semibold">
            {t('comingSoon', 'Coming Soon')}
          </TitleLG>
          <ParagraphSM color="core-gray-400">
            <Switch>
              <Case condition={isGuildMember}>
                {t('guildMembersLikeYou', 'Guild members like you made these releases happen.')}
              </Case>
              <Default>
                {t(
                  'guildMembersMakePossible',
                  'Guild members have made these releases happen. Join the guild today to help bring to life more shows that amplify light!',
                )}
              </Default>
            </Switch>
          </ParagraphSM>
          <When condition={!isGuildMember}>
            <div className="mt-4">
              <GuildEmailInput variant="left-white" hasUser={isLoggedIn} isCentered={false} />
            </div>
          </When>
          <When condition={!isLoggedIn}>
            <LabelXS>
              <Translate t={t} i18nKey="logInIfGuildMember">
                Already a member of the Angel Guild?&nbsp;
                <InternalLink
                  className="text-oxide-bright underline"
                  href={loginUrl}
                  onClick={() => trackLoginStarted('ComingSoonView')}
                >
                  Log In
                </InternalLink>
              </Translate>
            </LabelXS>
          </When>
          <div className="pt-8 lg:pt-12">
            <ul className="flex flex-col gap-12 border-l-2 border-core-gray-800 lg:gap-20">
              {comingSoonNodes.map((n, index) => {
                if (!n) return null
                return <ComingSoonItem key={n.id} node={n} itemIndex={index} />
              })}
            </ul>
          </div>
        </div>
      </PaddedContainer>
    </main>
  )
}

interface ComingSoonItemProps {
  node: ComingSoonNode
  itemIndex: number
}

const ComingSoonItem: React.FC<ComingSoonItemProps> = ({ node, itemIndex }) => {
  const { t } = useTranslate('coming-soon')
  const track = useSafeTrack()
  const [isLoadingImage, setLoadingImage] = useState(true)
  const onLoadComplete = useCallback(() => setLoadingImage(false), [])
  const { isGuildMember } = useGuildMember()
  const linkModel = useMemo(() => buildLinkTabModel(node), [node])
  const { trackInteraction } = useContentInteraction()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const contentId = node?.watchable?.id ?? ''
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (!contentId) return

    hoverStartTimeRef.current = Date.now()
  }, [contentId])

  const handleMouseLeave = useCallback(() => {
    if (!contentId || !hoverStartTimeRef.current || !linkModel?.imageUrl) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    if (dwellTime >= HOVER_THRESHOLD_MS && node?.watchable?.__typename) {
      const interaction = new DiscoverItemInteraction({
        id: contentId,
        __typename: node.watchable.__typename as 'ContentMovie' | 'ContentSeries' | 'ContentSpecial' | 'ContentEpisode',
        attribution: null,
      })
        .withSurface('generic_list', 'coming_soon')
        .withPlacement('title_list', 0, itemIndex, 'vertical_list')
        .withImageArtwork(linkModel.imageUrl, 'landscape_display')
        .withHover(dwellTime)

      trackInteraction(interaction)
    }

    hoverStartTimeRef.current = null
  }, [contentId, linkModel?.imageUrl, node?.watchable?.__typename, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    if (!contentId || !linkModel?.imageUrl || !node?.watchable?.__typename) return

    const interaction = new DiscoverItemInteraction({
      id: contentId,
      __typename: node.watchable.__typename as 'ContentMovie' | 'ContentSeries' | 'ContentSpecial' | 'ContentEpisode',
      attribution: null,
    })
      .withSurface('generic_list', 'coming_soon')
      .withPlacement('title_list', 0, itemIndex, 'vertical_list')
      .withImageArtwork(linkModel.imageUrl, 'landscape_display')
      .withPress()

    trackInteraction(interaction)

    // Also track the legacy analytics event
    if (linkModel?.track) {
      track(linkModel.track.eventName, linkModel.track.payload)
    }
  }, [contentId, linkModel, node?.watchable?.__typename, itemIndex, trackInteraction, track])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!contentId || hasTrackedImpression.current) return

    if (isVisible) {
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true

        if (linkModel?.imageUrl && node?.watchable?.__typename) {
          const interaction = new DiscoverItemInteraction({
            id: contentId,
            __typename: node.watchable.__typename as
              | 'ContentMovie'
              | 'ContentSeries'
              | 'ContentSpecial'
              | 'ContentEpisode',
            attribution: null,
          })
            .withSurface('generic_list', 'coming_soon')
            .withPlacement('title_list', 0, itemIndex, 'vertical_list')
            .withImageArtwork(linkModel.imageUrl, 'landscape_display')
            .withImpression()

          trackInteraction(interaction)
        }
      }, 1000)
    } else {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, contentId, linkModel?.imageUrl, node?.watchable?.__typename, trackInteraction, itemIndex])

  if (!node) return null
  if (!linkModel || !linkModel.imageUrl) return null

  const isTheatrical = node?.focus?.type === 'theatrical'
  const isGuildEarlyAccess = node?.focus?.type === 'guild-access'
  const isPublicAccess = node?.focus?.type === 'public-access'
  const hasFocusDate = !!node?.focus?.date

  const title = node.title ?? node?.watchable?.name ?? node?.project?.name

  const focusHeader = node.header

  return (
    <LazyHydrate whenVisible>
      <li>
        <div className="relative">
          <div className="absolute left-[-5px] top-[6px] h-2 w-2 rounded-full border-2 border-white bg-black"></div>

          <div ref={cardRef} className="ml-5 lg:ml-7">
            <div className="mb-4">
              <TitleXS as={AsSpan} className="lg:photon-title-xs uppercase" weight="normal">
                {focusHeader}
              </TitleXS>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row">
              <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <InternalLink href={linkModel.linkUrl} onClick={handleClick}>
                  <div
                    className={classNames(
                      'relative cursor-pointer overflow-hidden rounded-xl bg-cover duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.05]',
                    )}
                  >
                    <div className={classNames(['bg-black', isLoadingImage ? 'animate-pulse' : ''])}>
                      <div className="aspect-h-9 aspect-w-16 relative w-full lg:w-[560px] xl:w-[790px]">
                        <Image
                          className="absolute left-0 top-0 h-full w-full rounded-xl object-cover"
                          src={getCloudinaryImageUrl({ path: linkModel.imageUrl, width: 790, height: 444 })}
                          alt={linkModel.alt || ''}
                          onLoadingComplete={onLoadComplete}
                          width={790}
                          height={444}
                        />
                      </div>
                    </div>
                  </div>
                </InternalLink>
              </div>
              <div className="flex w-full flex-col gap-4 rounded-xl bg-core-gray-900 p-6">
                <HeadingXS className="lg:photon-heading-sm" as={AsH2}>
                  {title}
                </HeadingXS>
                <ParagraphMD>{node.watchable.description}</ParagraphMD>
                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <Switch>
                      <Case condition={hasFocusDate && isTheatrical}>
                        <TicketIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">
                          {t('inTheatersOnDate', 'In theaters on {{date}}', { date: focusHeader })}
                        </CaptionMD>
                      </Case>
                      <Case condition={hasFocusDate && isGuildEarlyAccess}>
                        <PlaySquareIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">
                          {t('streamingForGuildMembers', 'Streaming for Guild Members on {{date}}', {
                            date: focusHeader,
                          })}
                        </CaptionMD>
                      </Case>
                      <Case condition={hasFocusDate && isPublicAccess}>
                        <PlaySquareIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">
                          {t('streamingForEveryone', 'Streaming free for everyone on {{date}}', {
                            date: focusHeader,
                          })}
                        </CaptionMD>
                      </Case>
                      <Default>
                        <Switch>
                          <Case condition={isTheatrical}>
                            <TicketIcon color="info-200" size={24} />
                          </Case>
                          <Case condition={isGuildEarlyAccess || isPublicAccess}>
                            <PlaySquareIcon color="info-200" size={24} />
                          </Case>
                        </Switch>
                        <CaptionMD color="info-200">{focusHeader}</CaptionMD>
                      </Default>
                    </Switch>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <Switch>
                      <Case condition={isGuildMember && (isGuildEarlyAccess || isTheatrical)}>
                        <CircleCheckIcon color1="core-gray-925" color2="success-200" size={24} />
                        <CaptionMD color="success-200">
                          {t('includedWithGuildMembership', 'Included with your Guild membership')}
                        </CaptionMD>
                      </Case>
                      <Case condition={!isGuildMember && (isGuildEarlyAccess || isTheatrical)}>
                        <GuildIconMono color="core-warning-200" size={24} />
                        <CaptionMD color="core-warning-200">
                          {t('includedForGuildMembers', 'Included for members of the Angel Guild')}
                        </CaptionMD>
                      </Case>
                    </Switch>
                  </div>
                </div>
                <div className="mt-8 flex flex-row gap-8">
                  <When condition={!isTheatrical}>
                    <LinkButton href={linkModel.linkUrl} variant="white" outline>
                      <CircleInfoIcon className="mr-2" color="white" size={24} />
                      {t('viewDetails', 'View Details')}
                    </LinkButton>
                  </When>
                  <When condition={isTheatrical}>
                    <LinkButton href={linkModel.linkUrl} variant="white" outline>
                      <TicketIcon className="mr-2" color="white" size={24} />
                      {isGuildMember ? t('claimTickets', 'Claim Tickets') : t('getShowtimes', 'Get Showtimes')}
                    </LinkButton>
                  </When>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    </LazyHydrate>
  )
}
